/**
 * Automation Resource
 *
 * Thin 1:1 mapping to the automation API. No business logic, no default
 * parameter injection. The SDK passes params as-is.
 */

import type { HttpLayer } from '../http.js';
import type {
  Automation,
  CreateAutomationRequest,
  UpdateAutomationRequest,
  AutomationDraft,
  AutomationVersion,
  AutomationRun,
  AutomationStepExecution,
  PublishVersionRequest,
  RollbackRequest,
  TriggerRunRequest,
  DryRunRequest,
  DryRunResult,
  CompareVersionsResult,
  ValidationResult,
  AutomationTemplate,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  PublishTemplateRequest,
  AutomationTemplateVersion,
  InstantiateTemplateRequest,
  SaveAsTemplateRequest,
  ListTemplatesParams,
  ListFeedParams,
  DispatchEventRequest,
  DispatchEventResult,
} from '../types/index.js';

const prefix = '/v1/automations';

export class AutomationResource {
  constructor(private http: HttpLayer) {}

  // --- Definition CRUD ---

  async create(params: CreateAutomationRequest): Promise<Automation> {
    return this.http.post(`${prefix}`, params);
  }

  async list(): Promise<Automation[]> {
    return this.http.get(`${prefix}`);
  }

  async listFeed(params?: ListFeedParams): Promise<AutomationRun[]> {
    return this.http.get(`${prefix}/feed`, params);
  }

  async get(automationId: string): Promise<Automation> {
    return this.http.get(`${prefix}/${automationId}`);
  }

  async update(
    automationId: string,
    params: UpdateAutomationRequest,
  ): Promise<Automation> {
    return this.http.patch(`${prefix}/${automationId}`, params);
  }

  async delete(automationId: string): Promise<Automation> {
    return this.http.delete(`${prefix}/${automationId}`);
  }

  // --- Draft ---

  async saveDraft(
    automationId: string,
    draft: Record<string, unknown>,
  ): Promise<AutomationDraft> {
    return this.http.put(`${prefix}/${automationId}/draft`, draft);
  }

  async getDraft(automationId: string): Promise<AutomationDraft> {
    return this.http.get(`${prefix}/${automationId}/draft`);
  }

  async validateDraft(automationId: string): Promise<ValidationResult> {
    return this.http.post(`${prefix}/${automationId}/validate`);
  }

  // --- Versions ---

  async publish(
    automationId: string,
    params?: PublishVersionRequest,
  ): Promise<AutomationVersion> {
    return this.http.post(`${prefix}/${automationId}/publish`, params);
  }

  async compareVersions(
    automationId: string,
    v1: number,
    v2: number,
  ): Promise<CompareVersionsResult> {
    return this.http.get(`${prefix}/${automationId}/versions/compare`, {
      v1,
      v2,
    });
  }

  async listVersions(automationId: string): Promise<AutomationVersion[]> {
    return this.http.get(`${prefix}/${automationId}/versions`);
  }

  async getVersion(
    automationId: string,
    versionNumber: number,
  ): Promise<AutomationVersion> {
    return this.http.get(
      `${prefix}/${automationId}/versions/${versionNumber}`,
    );
  }

  async rollback(
    automationId: string,
    params: RollbackRequest,
  ): Promise<AutomationVersion> {
    return this.http.post(`${prefix}/${automationId}/rollback`, params);
  }

  // --- Runs ---

  async triggerRun(
    automationId: string,
    params?: TriggerRunRequest,
  ): Promise<AutomationRun> {
    return this.http.post(`${prefix}/${automationId}/run`, params);
  }

  async listRuns(automationId: string): Promise<AutomationRun[]> {
    return this.http.get(`${prefix}/${automationId}/runs`);
  }

  async getRun(
    automationId: string,
    runId: string,
  ): Promise<AutomationRun> {
    return this.http.get(`${prefix}/${automationId}/runs/${runId}`);
  }

  async cancelRun(
    automationId: string,
    runId: string,
  ): Promise<AutomationRun> {
    return this.http.post(
      `${prefix}/${automationId}/runs/${runId}/cancel`,
    );
  }

  async listStepExecutions(
    automationId: string,
    runId: string,
  ): Promise<AutomationStepExecution[]> {
    return this.http.get(
      `${prefix}/${automationId}/runs/${runId}/steps`,
    );
  }

  // --- Dry Run ---

  async dryRun(
    automationId: string,
    params?: DryRunRequest,
  ): Promise<DryRunResult> {
    return this.http.post(`${prefix}/${automationId}/dry-run`, params);
  }

  // --- Admin ---

  async dispatchEvent(params: DispatchEventRequest): Promise<DispatchEventResult> {
    return this.http.post('/v1/admin/automations/dispatch-event', params);
  }

  // --- Templates ---

  async createTemplate(params: CreateTemplateRequest): Promise<AutomationTemplate> {
    return this.http.post(`${prefix}/templates`, params);
  }

  async listTemplates(params?: ListTemplatesParams): Promise<AutomationTemplate[]> {
    return this.http.get(`${prefix}/templates`, params);
  }

  async getTemplate(templateId: string): Promise<AutomationTemplate> {
    return this.http.get(`${prefix}/templates/${templateId}`);
  }

  async updateTemplate(
    templateId: string,
    params: UpdateTemplateRequest,
  ): Promise<AutomationTemplate> {
    return this.http.put(`${prefix}/templates/${templateId}`, params);
  }

  async deleteTemplate(templateId: string): Promise<AutomationTemplate> {
    return this.http.delete(`${prefix}/templates/${templateId}`);
  }

  async publishTemplate(
    templateId: string,
    params: PublishTemplateRequest,
  ): Promise<AutomationTemplateVersion> {
    return this.http.post(
      `${prefix}/templates/${templateId}/publish`,
      params,
    );
  }

  async listTemplateVersions(
    templateId: string,
  ): Promise<AutomationTemplateVersion[]> {
    return this.http.get(`${prefix}/templates/${templateId}/versions`);
  }

  async getTemplateVersion(
    templateId: string,
    versionNumber: number,
  ): Promise<AutomationTemplateVersion> {
    return this.http.get(
      `${prefix}/templates/${templateId}/versions/${versionNumber}`,
    );
  }

  async instantiateTemplate(
    templateId: string,
    params?: InstantiateTemplateRequest,
  ): Promise<Automation> {
    return this.http.post(
      `${prefix}/templates/${templateId}/instantiate`,
      params,
    );
  }

  async saveAsTemplate(
    automationId: string,
    params: SaveAsTemplateRequest,
  ): Promise<AutomationTemplate> {
    return this.http.post(
      `${prefix}/templates/from-automation?automation_id=${encodeURIComponent(automationId)}`,
      params,
    );
  }
}
